// ComplaintDetailsStep.tsx
import { useComplaintConfigurations } from "@features/cx/complaints/hooks";
import { StepHelpers } from "@shared/components/Stepper";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "@shared/hooks/use-toast";

interface ComplaintDetailsStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export function ComplaintDetailsStep({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}: ComplaintDetailsStepProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Use the utility request configuration hook
  const {
    data: complaintConfigs,
    isLoading,
    error,
  } = useComplaintConfigurations(remoteUtilityId, searchTerm);

  // Get selected complaint from step helpers
  const [selectedComplaint, setSelectedComplaint] = useState<any>(null);

  // Load saved complaint selection on component mount
  useEffect(() => {
    if (stepHelpers) {
      const savedData = stepHelpers.getStepData(currentStepIndex);
      if (savedData?.selectedComplaint) {
        setSelectedComplaint(savedData.selectedComplaint);
        console.log("Loaded saved complaint:", savedData.selectedComplaint);
      }
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to assign colors based on complaint type
  const getComplaintColor = (complaintType: string[]): string => {
    const colorMap: Record<string, string> = {
      water: "blue",
      wastewater: "brown",
      metering: "green",
      connection: "green",
      maintenance: "blue",
      repair: "red",
      emergency: "red",
      inspection: "purple",
      testing: "purple",
      installation: "green",
      replacement: "orange",
      upgrade: "blue",
      transfer: "yellow",
      temporary: "orange",
      routine: "green",
      preventative: "blue",
      equipment: "purple",
    };

    for (const [key, color] of Object.entries(colorMap)) {
      if (complaintType.includes(key)) {
        return color;
      }
    }
    return "blue"; // default color
  };

  // Transform API data to match component expectations
  const transformedComplaints = useMemo(() => {
    return (
      complaintConfigs?.map((config) => ({
        id: String(config.configurationCode || config.id),
        name: config.name,
        complaintName: config.name,
        description: config.longDescription || config.supportRequestTypeDisplay,
        severityLevel: config.extraData?.severityLevelDisplay,
        color: getComplaintColor(config.productCode),
        utilityComplaint: config.productCode.join(", "),
        complaintType: config.supportRequestTypeDisplay,
        category: config.supportRequestTypeDisplay,
        productCode: config.productCode,
        ...config,
      })) || []
    );
  }, [complaintConfigs]);

  const handleComplaintSelect = (complaint: any) => {
    console.log("Complaint being selected:", complaint);
    setSelectedComplaint(complaint);

    // Save selected complaint using step helpers
    if (stepHelpers) {
      const stepData = {
        selectedComplaint: complaint,
        complaintId: complaint.id,
        complaintName: complaint.name,
        complaintCharge: complaint.price,
        complaintType: complaint.complaintType,
        utilityComplaint: complaint.utilityComplaint,
        productCode: complaint.productCode,
        timestamp: new Date().toISOString(),
        stepIndex: currentStepIndex,
        version: complaint.version,
      };

      stepHelpers.setStepData(currentStepIndex, stepData);
    }

    toast({
      title: "Success",
      description: `Complaint "${complaint.name}" selected successfully`,
      variant: "default",
    });
  };

  // Check if a complaint is selected
  const isComplaintSelected = (complaintId: string | number) => {
    const result =
      selectedComplaint && String(selectedComplaint.id) === String(complaintId);
    console.log("Checking selection:", {
      selectedComplaintId: selectedComplaint?.id,
      complaintId,
      result,
    });
    return result;
  };

  const handleNext = () => {
    if (!selectedComplaint) {
      toast({
        title: "Error",
        description: "Please select a complaint to continue",
        variant: "destructive",
      });
      return;
    }
    logTimeUntilStep("Complaint Details");
    onNext();
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-destructive">
            Error loading complaints. Please try again.
          </p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600">
          Browse all available complaints and select the one you need.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Search complaints..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Complaint Cards */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 justify-between">
          {transformedComplaints.map((complaint) => (
            <Card
              key={complaint.id}
              className={`flex flex-col justify-between h-full cursor-pointer transition-all hover:shadow-md border-l-4 ${
                isComplaintSelected(complaint.id)
                  ? "border-primary bg-primary/5 shadow-md ring-2 ring-primary"
                  : `border-l-${complaint.color}-500`
              }`}
              onClick={() => handleComplaintSelect(complaint)}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base font-medium">
                    {complaint.name}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs capitalize">
                    {complaint.category}
                  </Badge>
                </div>
                <CardDescription className="text-sm text-muted-foreground">
                  {complaint.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="mt-auto pt-0">
                <div className="flex justify-between items-end w-full">
                  <Button
                    size="sm"
                    variant={
                      isComplaintSelected(complaint.id) ? "default" : "outline"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleComplaintSelect(complaint);
                    }}
                  >
                    {isComplaintSelected(complaint.id) ? "Selected" : "Select"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && transformedComplaints.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            {searchTerm
              ? "No complaints found matching your search criteria."
              : "No complaints available at the moment."}
          </p>
        </div>
      )}
      {selectedComplaint && (
        <div className="mt-2 p-3 bg-primary/5 border border-primary/20 rounded-md">
          <p className="text-sm font-medium text-primary">
            Selected: {selectedComplaint.name}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onPrevious}>
          Back
        </Button>
        <Button onClick={handleNext} disabled={!selectedComplaint}>
          Continue to Details
        </Button>
      </div>
    </div>
  );
}
